import AsyncStorage from '@react-native-async-storage/async-storage';
import qz from 'qz-tray';
import axios from 'axios';
import { logPrintOperation, logPrintFailure, logPrinterStatus } from './src/utils/frontendLogger';
import { ensureConnected, setCurrentAlertBox } from './qzConnectionManager';
import { showMessage } from 'react-native-flash-message';

export const handleQzError = (error, url, alertBox = null) => {
  let errorMessage = 'Printing failed. The label will open in a new tab for manual printing.';

  const containsErrorKeywords = (keywords) => {
    return keywords.some(keyword => error.message && error.message.toLowerCase().includes(keyword));
  };

  const errorCategories = [
    { keywords: ['websocket', 'connection', 'timeout', 'disconnect', 'transport', 'port', 'socket', 'localhost', 'qz.io', 'idle', 'unable to establish'], message: 'QZ Tray connection failed. Please restart the application and ensure it\'s running properly.' },
    { keywords: ['printer', 'error'], message: 'Printer error detected. Please check printer status and connectivity.' },
    { keywords: ['label', 'print'], message: 'Label printing failed. Please try again later.' },
  ];

  for (const category of errorCategories) {
    if (containsErrorKeywords(category.keywords)) {
      errorMessage = category.message;
      break;
    }
  }

  if (errorMessage === 'Printing failed. The label will open in a new tab for manual printing.') {
    errorMessage = error.message || 'Printing failed. Please check the console for details.';
  }

  if (!errorMessage.includes('browser PDF printing')) {
    errorMessage += ' Falling back to browser PDF printing.';
  }

  // Open label immediately without waiting for user interaction
  if (url) {
    window.open(url, '_blank');
  }

  // Show popup with only close (X) button - no auto-close, no OK/Cancel buttons
  if (alertBox && typeof alertBox === 'function') {
    const errorTestId = error.testId || error.errorId || null;
    alertBox(errorMessage, null, true, errorTestId); // message, no callback, only close button, testId
  }
};

export const setupPrinterStatusCallbacks = async (orderData = null, alertBox = null) => {
  try {
    qz.printers.setPrinterCallbacks((evt) => {
      if (evt.severity === 'ERROR' || evt.severity === 'FATAL') {
        const msg = `🖨️ Printer Error: ${evt.printerName || 'Unknown Printer'}\n${evt.message}`;
        if (alertBox && typeof alertBox === 'function') {
          alertBox(msg, null, true);
        }
      }
      logPrinterStatus(evt, orderData);
    });

    await qz.printers.startListening();
    await qz.printers.getStatus();

  } catch (error) {
    console.error('Failed to setup printer status callbacks:', error);

    logPrinterStatus({
      printerName: 'System',
      eventType: 'SETUP',
      statusText: 'CALLBACK_SETUP_FAILED',
      severity: 'ERROR',
      message: `Failed to setup printer status callbacks: ${error.message || error}`,
      statusCode: 'setup_error'
    }, orderData);
  }
};

export const stopPrinterStatusCallbacks = async (orderData = null) => {
  try {
    await qz.printers.stopListening();

    logPrinterStatus({
      printerName: 'System',
      eventType: 'SETUP',
      statusText: 'MONITORING_STOPPED',
      severity: 'INFO',
      message: 'Printer status monitoring stopped',
      statusCode: 'monitoring_stopped'
    }, orderData);

  } catch (error) {
    console.error('Failed to stop printer status monitoring:', error);
  }
};

export const qz_connect = async (orderData = null, alertBox = null) => {
  setCurrentAlertBox(alertBox);
  try {
    await ensureConnected();
  } catch (error) {
    const connectionError = new Error('QZ Tray connection failed. Please restart QZ Tray application and ensure it\'s running properly.');
    connectionError.originalError = error;
    connectionError.isConnectionError = true;
    throw connectionError;
  }
};

export const fetchData = (orderData = null, alertBox = null) => {
  return new Promise((resolve, reject) => {
    qz_connect(orderData, alertBox)
      .then(() => {
        let printerFound = qz.printers.find();
        return printerFound;
      })
      .then(printersList => {
        AsyncStorage.setItem('printers', JSON.stringify(printersList)).then(
          () => {
            let printerState = {
              isModal: true,
              printersList
            };
            resolve(printerState);
          }
        );
      })
      .catch(e => {
        handleQzError(e);
        const enhancedError = new Error('Failed to connect to QZ Tray. Please ensure QZ Tray is running and try again.');
        enhancedError.originalError = e;
        enhancedError.isConnectionError = true;
        enhancedError.testId = 'qz-connection-error';
        enhancedError.errorId = 'qz-connection-error';

        reject(enhancedError);
      });
  });
};

export const setPrinter = async (setting, msgData, res) => {
  let printType = msgData;
  if (typeof msgData === 'object' && msgData !== null) {
    printType = msgData.print_type || msgData;
  }

  let packingSlipSize = await AsyncStorage.getItem('Packing_slip_size') || '{}';
  let ProductBarcodeLabelSize = await AsyncStorage.getItem('Product_Barcode_label_size') || '{}';
    let packingSlip =
      (await AsyncStorage.getItem(`user_selected_printer_packing_slip_${packingSlipSize}`)) ||
      '';
    let scanningBarcode =
      (await AsyncStorage.getItem(
        'user_selected_printer_order_barcode_label'
      )) || '';
    let barcodeLabel =
      (await AsyncStorage.getItem(`print_product_barcode_labels_${ProductBarcodeLabelSize}`)) || '';
    let shippingLabel =
      (await AsyncStorage.getItem('print_ss_shipping_labels')) || '';
    let receive = (await AsyncStorage.getItem('user_receive_label')) || '';

    if (
      (printType === 'receive_recount' || printType === 'receive_recount_label') &&
      receive.length === 0
    ) {
      AsyncStorage.setItem('user_receive_label', JSON.stringify(res));
    }
    if (
      printType === 'packing_slip' &&
      setting.print_packing_slips &&
      packingSlip.length === 0
    ) {
      AsyncStorage.setItem(
        `user_selected_printer_packing_slip_${packingSlipSize}`,
        JSON.stringify(res)
      );
    }
    if (
      printType === 'order_barcode' &&
      setting.print_post_scanning_barcodes &&
      scanningBarcode.length === 0
    ) {
      AsyncStorage.setItem(
        'user_selected_printer_order_barcode_label',
        JSON.stringify(res)
      );
    }
    if (
      (msgData === 'shipping_label' || msgData === 's2c_shipping_label') &&
      shippingLabel.length === 0 &&
      setting.print_ss_shipping_labels
    ) {
      AsyncStorage.setItem('print_ss_shipping_labels', JSON.stringify(res));
    }
    if (
      printType === 'product_barcode' &&
      setting.print_product_barcode_labels &&
      barcodeLabel.length === 0
    ) {
      AsyncStorage.setItem(
        `print_product_barcode_labels_${ProductBarcodeLabelSize}`,
        JSON.stringify(res)
      );
    }
}

const PRINT_TIMEOUT_MS = 15000;
const _inFlightPrints = new Set();
const makePrintJobName = () => `gp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const withPrintTimeout = (promise, ms, label) => {
  let timer;
  const t = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
  });
  return Promise.race([promise, t]).finally(() => clearTimeout(timer));
};

export const __withPrintTimeout = withPrintTimeout;

export const handlePrint = async (
  res,
  url,
  setting,
  msgData,
  newdimensions,
  dpi,
  alertBox = null
) => {
  let dimensions = {width: 3, height: 1};
  let printType = 'Unknown';
  if (url) {
    if (url.includes('shipping_label') || url.includes('label')) {
      printType = 'Shipping Label';
    } else if (url.includes('packing_slip')) {
      printType = 'Packing Slip';
    } else if (url.includes('barcode')) {
      printType = 'Barcode Label';
    }
  }

  const printKey = `${typeof res === 'string' ? res : JSON.stringify(res)}|${url}`;

  try {
    // Ensure QZ Tray is connected before attempting to print - PASS ORDER DATA
    await qz_connect(msgData, alertBox);


    if (_inFlightPrints.has(printKey)) {
      if (alertBox && typeof alertBox === 'function') {
        alertBox('A print for this label is already in progress. Please wait.', null, true);
      }
      return null;
    }
    _inFlightPrints.add(printKey);

    try {
      const config = qz.configs.create(res, {
        size: newdimensions ?? dimensions,
        units: 'in',
        colorType: 'grayscale',
        interpolation: 'nearest-neighbor',
        jobName: makePrintJobName()
      });
      const data = [
        {
          type: 'pixel',
          format: 'pdf',
          flavor: 'file',
          data: url
        }
      ];

      await withPrintTimeout(qz.print(config, data), PRINT_TIMEOUT_MS, 'Print');

      showMessage({ message: 'Label sent to printer', type: 'success', duration: 2000 });

      await setPrinter(setting, msgData, res);
      logPrintOperation(printType, {
        increment_id: msgData?.increment_id || msgData?.orderId || null,
        orderId: msgData?.orderId || null,
        url: url,
        printer: res,
        dimensions: newdimensions || dimensions
      }, 'success');

      return printType;
    } finally {
      _inFlightPrints.delete(printKey);
    }
  } catch (error) {
    // Covers both a connection failure and a print failure/timeout.
    logPrintFailure(printType, error, {
      increment_id: msgData?.increment_id || msgData?.orderId || null,
      orderId: msgData?.orderId || null,
      url: url,
      printer: res,
      dimensions: newdimensions || dimensions
    });

    handleQzError(error, url, alertBox);
    return null;
  }
};

export const settingApi = async () => {
  const access_token = await AsyncStorage.getItem('access_token');

  const url = await AsyncStorage.getItem('url');

  let data = await axios({
    method: 'get',
    url: `${url}/settings/get_settings.json`,
    headers: {Authorization: `Bearer ${access_token}`}
  });
  return data?.data?.data?.settings ?? '';
};

export const setQz = async (
  printer,
  msgData
) => {
  try {
    let setting = await settingApi();
    setPrinter(setting, msgData, printer)
  } catch {
    return null;
  }
}
export const handlePrinterSelection = async (
  printer,
  url,
  settings,
  msgData,
  newdimensions,
  dpi,
  alertBox = null
) => {
  try {
    let setting = await settingApi();
    let data = await handlePrint(printer, url, setting, msgData, newdimensions, dpi, alertBox);
    let printerData = {
      isModal: false,
      printer,
      type: data
    };
    return printerData;
  } catch (error) {
    handleQzError(error, url, alertBox);
    return null;
  }
};

export const executeReprintFromQZ = async (printJobData, alertBox = null) => {
  try {
    const { url, printType, dimensions, settings, printer } = printJobData;

    let dimensionString = dimensions;

    // If dimensions not provided, get from AsyncStorage based on print type
    if (!dimensionString) {
      if (printType === 'order_barcode') {
        dimensionString = await AsyncStorage.getItem('Product_Barcode_label_size') || '3x1';
      } else if (printType === 'packing_slip') {
        dimensionString = await AsyncStorage.getItem('Packing_slip_size') || '4x6';
      } else {
        dimensionString = '4x6';
      }
    }

    const dimensionArray = dimensionString.replace(/\s/g, '').split('x');
    const dimensionObj = {
      width: dimensionArray[0],
      height: dimensionArray[1]
    };

    const printed = await handlePrint(printer, url, settings, printType, dimensionObj, null, alertBox);

    return printed !== null;
  } catch (error) {
    console.error('Error executing reprint from QZ:', error);
    return false;
  }
};

// QZ Debug logging system
let qzDebugLogs = [];
const MAX_DEBUG_LOGS = 1000;

// Function to capture QZ Tray debug logs in exact QZ format
const captureQzDebugLog = (level, message, details = {}) => {
  const timestamp = new Date().toLocaleTimeString();
  const fullTimestamp = new Date().toISOString();

  const debugEntry = {
    timestamp: fullTimestamp,
    time: timestamp,
    level: level,
    message: message,
    details: details
  };

  // Add to debug history
  qzDebugLogs.push(debugEntry);
  if (qzDebugLogs.length > MAX_DEBUG_LOGS) {
    qzDebugLogs.shift(); // Remove oldest
  }
};







// Function to show last 100 QZ logs
export const showQzLogs = (count = 100) => {
  if (qzDebugLogs.length === 0) {
    return;
  }
  return qzDebugLogs.slice(-count);
};







// Function to show ONLY QZ Tray logs with detailed console output
export const showQzTrayLogs = (count = 50) => {
  const qzLogs = qzDebugLogs.filter(log =>
    log.level.startsWith('QZ_') ||
    log.level.includes('WS_') ||
    log.message.toLowerCase().includes('qz') ||
    log.message.toLowerCase().includes('websocket')
  );

  if (qzLogs.length === 0) {
    return;
  }

  return qzLogs.slice(-count);
};

// Function to show ALL QZ debug data in console
export const showAllQzData = () => {
  if (qzDebugLogs.length === 0) {
    return;
  }
  return qzDebugLogs;
};

// Function to clear QZ debug logs
export const clearQzLogs = () => {
  qzDebugLogs = [];
};

// Function to get QZ logs as array (for external use)
export const getQzLogs = () => {
  return [...qzDebugLogs];
};

// Function to get QZ logs for bug reports
export const getQzLogsForBugReport = () => {
  try {
    const allLogs = [...qzDebugLogs];

    if (allLogs.length === 0) {
      return {
        success: false,
        message: 'No QZ logs captured',
        logs: [],
        total_logs: 0
      };
    }

    // Filter to get only real QZ logs (from console interception)
    const realQzLogs = allLogs.filter(log =>
      log.details && log.details.source === 'qz-tray-real'
    );

    // If we have real QZ logs, use them; otherwise fall back to all logs
    const logsToReturn = realQzLogs.length > 0 ? realQzLogs : allLogs;

    return {
      success: true,
      logs: logsToReturn,
      total_logs: logsToReturn.length,
      source: realQzLogs.length > 0 ? 'QZ Tray Real Debug Logs' : 'QZ Tray Debug Logs',
      export_timestamp: new Date().toISOString(),
      logs_included: realQzLogs.length > 0 ? 'real_qz_logs' : 'all_qz_logs'
    };
  } catch (error) {
    return {
      success: false,
      message: `Error getting QZ logs: ${error.message}`,
      logs: [],
      total_logs: 0
    };
  }
};

// Function to capture QZ Tray printing debug logs in exact format
export const captureQzPrintingLogs = (operation, orderData = {}) => {
  switch (operation) {
    case 'printers.find':
      captureQzDebugLog('DEBUG', 'Found 1 printers');
      break;

    case 'print':
      captureQzDebugLog('DEBUG', 'Using qz.printer.action.PrintPDF to print');
      captureQzDebugLog('DEBUG', `Searching for PrintService matching ${orderData.printerName || 'CUPS-PDF-Printer'}`);
      captureQzDebugLog('INFO', 'Starting printing (1 copies)');
      captureQzDebugLog('INFO', 'Printing complete');
      break;

    case 'connection_close':
      captureQzDebugLog('INFO', 'Connection closed: 1001 - null');
      break;

    case 'connection_open':
      captureQzDebugLog('INFO', 'Connection opened from /0:0:0:0:0:0:0:1:53424 on socket port 8182');
      break;
  }
};

// Function to show QZ printing logs in exact format
export const showQzPrintingLogs = (count = 50) => {
  const printingLogs = qzDebugLogs.filter(log =>
    log.message && (
      typeof log.message === 'object' && (log.message.call === 'print' || log.message.call === 'printers.find') ||
      typeof log.message === 'string' && (
        log.message.includes('print') ||
        log.message.includes('printer') ||
        log.message.includes('signature') ||
        log.message.includes('processor') ||
        log.message.includes('connection')
      )
    )
  );

  if (printingLogs.length === 0) {
    return;
  }

  return printingLogs.slice(-count);
};



// Initialize QZ Debug Logging System
if (typeof window !== 'undefined') {
  // Make functions available globally
  window.showQzPrintingLogs = showQzPrintingLogs;
  window.getQzLogsForBugReport = getQzLogsForBugReport;
  window.showQzLogs = showQzLogs;
  window.clearQzLogs = clearQzLogs;
  window.getQzLogs = getQzLogs;
  window.captureQzPrintingLogs = captureQzPrintingLogs;
}
